import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ExperimentForm = ({ journeyId, token, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    experiment_type: 'user_research',
    experiment_steps: '',
    data_collection_method: '',
    success_criteria: '',
    results: '',
    key_findings: '',
  });

  const [experimentData, setExperimentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);

  const experimentTypes = [
    { value: 'user_research', label: 'User Research (interviews, surveys)' },
    { value: 'prototype', label: 'Prototype & Testing' },
    { value: 'ab_test', label: 'A/B Test' },
    { value: 'pilot', label: 'Pilot Program' },
    { value: 'data_analysis', label: 'Data Analysis' },
    { value: 'focus_group', label: 'Focus Group' }
  ];

  useEffect(() => {
    loadExperiment();
  }, [journeyId, token]);

  const loadExperiment = async () => {
    try {
      const response = await axios.get(
        `http://localhost:5000/api/journeys/${journeyId}/experiments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.experiments) {
        setExperimentData(response.data.experiments);
        setFormData(response.data.experiments);
      }
    } catch (err) {
      console.error('Load experiment error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const isFormValid = () => {
    return (
      formData.experiment_type &&
      formData.experiment_steps.trim().length >= 20 &&
      formData.data_collection_method.trim().length >= 10 &&
      formData.success_criteria.trim().length >= 10 &&
      formData.results.trim().length >= 10
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const payload = {
        experiment_type: formData.experiment_type,
        experiment_steps: formData.experiment_steps.trim(),
        data_collection_method: formData.data_collection_method.trim(),
        success_criteria: formData.success_criteria.trim(),
        results: formData.results.trim(),
        key_findings: formData.key_findings.trim() || null,
      };

      const response = await axios.post(
        `http://localhost:5000/api/journeys/${journeyId}/experiments`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      setSuccessMessage('Experiment results submitted successfully!');
      setExperimentData(response.data.experiment);
      setIsEditing(false);
      if (onSuccess) {
        setTimeout(() => onSuccess(response.data.experiment), 1500);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to submit experiment.';
      setErrorMessage(errorMsg);
      console.error('Experiment submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      experiment_type: 'user_research',
      experiment_steps: '',
      data_collection_method: '',
      success_criteria: '',
      results: '',
      key_findings: '',
    });
  };

  if (experimentData && !isEditing) {
    return (
      <div className="form-card" style={{ backgroundColor: '#f9f9f9', opacity: 0.95 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 className="form-title">🧪 Design & Run Experiment</h2>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#d4edda',
              color: '#155724',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: '600',
              marginTop: '8px',
            }}>
              ✓ COMPLETE
            </div>
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => setShowConfirmEdit(true)}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            ✏️ Edit
          </button>
        </div>

        {successMessage && <div className="form-success">{successMessage}</div>}
        {errorMessage && <div className="form-error">{errorMessage}</div>}

        {showConfirmEdit && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }} onClick={() => setShowConfirmEdit(false)}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '400px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginBottom: '12px', color: '#2c3e50' }}>Edit Completed Experiment?</h3>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                This experiment has already been marked complete. Changes will be logged in the audit trail.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setShowConfirmEdit(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setIsEditing(true);
                    setShowConfirmEdit(false);
                  }}
                >
                  Continue Editing
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: '20px' }}>
          {formData.experiment_steps && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Experiment Steps</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.experiment_steps}</p>
            </div>
          )}
          {formData.data_collection_method && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Data Collection Method</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.data_collection_method}</p>
            </div>
          )}
          {formData.success_criteria && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Success Criteria</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.success_criteria}</p>
            </div>
          )}
          {formData.results && (
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Results</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.results}</p>
            </div>
          )}
          {formData.key_findings && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Key Findings</h4>
              <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.key_findings}</p>
            </div>
          )}
        </div>

        <div className="form-actions">
          {onCancel && <button className="btn btn-secondary" onClick={onCancel}>Back</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="form-card">
      <h2 className="form-title">🧪 Design & Run Experiment</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>Step 4 of 5: Test your hypotheses and gather evidence</p>

      {successMessage && <div className="form-success">{successMessage}</div>}
      {errorMessage && <div className="form-error">{errorMessage}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Experiment Type *</label>
          <select name="experiment_type" value={formData.experiment_type} onChange={handleInputChange} required>
            <option value="">Select experiment type...</option>
            {experimentTypes.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Experiment Steps *</label>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '10px' }}>Describe how you'll run the experiment (min 20 chars)</p>
          <textarea
            name="experiment_steps"
            value={formData.experiment_steps}
            onChange={handleInputChange}
            maxLength="2000"
            style={{ minHeight: '100px' }}
          />
          <div className="char-count">{formData.experiment_steps.length}/2000 characters</div>
        </div>

        <div className="form-group">
          <label>Data Collection Method *</label>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '10px' }}>How will you collect data? (min 10 chars)</p>
          <textarea
            name="data_collection_method"
            value={formData.data_collection_method}
            onChange={handleInputChange}
            maxLength="1000"
            style={{ minHeight: '80px' }}
          />
          <div className="char-count">{formData.data_collection_method.length}/1000 characters</div>
        </div>

        <div className="form-group">
          <label>Success Criteria *</label>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '10px' }}>What does success look like? (min 10 chars)</p>
          <textarea
            name="success_criteria"
            value={formData.success_criteria}
            onChange={handleInputChange}
            maxLength="1000"
            style={{ minHeight: '80px' }}
          />
          <div className="char-count">{formData.success_criteria.length}/1000 characters</div>
        </div>

        <div className="form-group">
          <label>What Happened (Results) *</label>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '10px' }}>Summarize what you observed/learned (min 10 chars)</p>
          <textarea
            name="results"
            value={formData.results}
            onChange={handleInputChange}
            maxLength="2000"
            style={{ minHeight: '120px' }}
          />
          <div className="char-count">{formData.results.length}/2000 characters</div>
        </div>

        <div className="form-group">
          <label>Key Findings & Insights</label>
          <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '10px' }}>What else did you learn? (optional)</p>
          <textarea
            name="key_findings"
            value={formData.key_findings}
            onChange={handleInputChange}
            maxLength="1000"
            style={{ minHeight: '80px' }}
          />
          <div className="char-count">{formData.key_findings.length}/1000 characters</div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={handleClear} disabled={loading}>
            Clear
          </button>
          {onCancel && (
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
              Back
            </button>
          )}
          <button type="submit" className="btn btn-primary" disabled={loading || !isFormValid()}>
            {loading ? <span className="loading">⏳</span> : 'Submit Experiment Results'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExperimentForm;
